// PATH: src/components/catalog/forms/SimpleEntityDialog.tsx
import * as React from "react";
import { FormControlLabel, Stack, Switch, TextField } from "@mui/material";
import { codeTaken, nextSortOrder, orderTaken, parseOptionalInt, type EntityKind } from "../catalogModel";
import { FormDialog } from "./FormDialog";

export interface SimpleValues {
  name: string;
  code: string;
  sort_order: number;
  is_active: boolean;
}

interface Sibling {
  id: string;
  code: string;
  sort_order?: number | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  entity: Extract<EntityKind, "stage" | "grade" | "track">;
  /** « Nouvelle étape », « Modifier le niveau » */
  title: string;
  context?: React.ReactNode;
  /** Élément édité ; absent = création. */
  initial?: (Sibling & { name: string; is_active?: boolean }) | null;
  siblings: Sibling[];
  /** Le rang est unique entre frères en base (étapes). */
  orderUnique?: boolean;
  /** Interrupteur « active » (filières). */
  withActive?: boolean;
  namePlaceholder?: string;
  codePlaceholder?: string;
  codeHelp?: string;
  successMessage: string;
  onSubmit: (v: SimpleValues) => Promise<void>;
  onSaved?: () => void;
}

/** Formulaire nom / code / rang : étapes, niveaux, filières. */
export const SimpleEntityDialog: React.FC<Props> = ({
  open,
  onClose,
  entity,
  title,
  context,
  initial,
  siblings,
  orderUnique,
  withActive,
  namePlaceholder,
  codePlaceholder,
  codeHelp,
  successMessage,
  onSubmit,
  onSaved,
}) => {
  const [name, setName] = React.useState("");
  const [code, setCode] = React.useState("");
  const [order, setOrder] = React.useState("");
  const [active, setActive] = React.useState(true);
  const [touched, setTouched] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setName(initial?.name ?? "");
    setCode(initial?.code ?? "");
    setOrder(String(initial ? initial.sort_order ?? 0 : nextSortOrder(siblings)));
    setActive(initial?.is_active ?? true);
    setTouched(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial?.id]);

  const parsedOrder = parseOptionalInt(order);
  const errors = {
    name: !name.trim() ? "Le nom est requis." : "",
    code: !code.trim()
      ? "Le code est requis."
      : codeTaken(siblings, code, initial?.id)
        ? "Ce code est déjà utilisé ici."
        : "",
    order:
      parsedOrder === null
        ? "Le rang est requis."
        : Number.isNaN(parsedOrder)
          ? "Un nombre entier."
          : orderUnique && orderTaken(siblings, parsedOrder, initial?.id)
            ? "Rang déjà occupé."
            : "",
  };
  const show = (k: keyof typeof errors) => (touched ? errors[k] : "");

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={title}
      context={context}
      entity={entity}
      submitLabel={initial ? "Enregistrer" : "Créer"}
      successMessage={successMessage}
      validate={() => {
        setTouched(true);
        return !errors.name && !errors.code && !errors.order;
      }}
      onSubmit={() =>
        onSubmit({ name: name.trim(), code: code.trim(), sort_order: parsedOrder ?? 0, is_active: active })
      }
      onSaved={onSaved}
    >
      <Stack spacing={2}>
        <TextField
          autoFocus
          label="Nom"
          required
          fullWidth
          value={name}
          placeholder={namePlaceholder}
          onChange={(e) => setName(e.target.value)}
          error={Boolean(show("name"))}
          helperText={show("name") || " "}
          slotProps={{ htmlInput: { dir: "auto" } }}
        />
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <TextField
            label="Code"
            required
            fullWidth
            value={code}
            placeholder={codePlaceholder}
            onChange={(e) => setCode(e.target.value)}
            error={Boolean(show("code"))}
            helperText={show("code") || codeHelp || " "}
            slotProps={{ htmlInput: { className: "mono", spellCheck: false } }}
          />
          <TextField
            label="Rang"
            required
            value={order}
            onChange={(e) => setOrder(e.target.value)}
            error={Boolean(show("order"))}
            helperText={show("order") || "Ordre d'affichage"}
            sx={{ width: 130, flexShrink: 0 }}
            slotProps={{ htmlInput: { inputMode: "numeric" } }}
          />
        </Stack>
        {withActive && (
          <FormControlLabel
            control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
            label="Active"
          />
        )}
      </Stack>
    </FormDialog>
  );
};

export default SimpleEntityDialog;
